// src/pages/ViewRecipientPage.js - Updated with visa support and conditional rendering
import React, { useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Paper, Divider, CircularProgress, Button, Stack,
  Chip, Alert, Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRecipientById } from '../slices/recipientSlice';
import { bankLabel, branchLabel } from '../utils/bankLabel';
import { motion } from 'framer-motion';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoIcon from '@mui/icons-material/Info';
import NumbersIcon from '@mui/icons-material/Numbers';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { exportRecipientToPrint } from '../utils/exportUtils';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { formatPhoneForWhatsApp } from '../utils/phoneUtils';

const MotionPaper = motion(Paper);

const WhatsAppButton = ({ recipient }) => {
  const phoneNumber = recipient.parent_phone

  if (!phoneNumber) return null;

  // بناء نص الرسالة بكل التفاصيل
  const buildMessage = (r) => {
    let message = `سند قبض من برنامج دفتر نون:\n` +
      `رقم السند: ${r.number || '-'}\n` +
      `اسم الطالب: ${r.student_name || '-'}\n` +
      `اسم ولي الأمر: ${r.parent_name || '-'}\n` +
      `هاتف ولي الأمر: ${r.parent_phone || '-'}\n` +
      `الصف: ${r.class_name || '-'}\n` +
      `المبلغ: ${r.amount || '-'} شيكل\n` +
      `طريقة الدفع: ${r.payment_type || '-'}\n` +
      `التاريخ: ${r.date || '-'}\n` +
      `الوقت: ${r.time_display || r.time || '-'}\n` +
      `منشئ السند: ${r.created_by_name || '-'}\n` +
      `حالة الاستلام: ${r.received ? 'تم الاستلام' : 'لم يتم الاستلام'}\n`;

    if (r.total_school_fee !== undefined && r.remaining_amount !== undefined) {
      message += `إجمالي الرسوم: ${r.total_school_fee} شيكل\n` +
        `الباقي على الطالب: ${r.remaining_amount} شيكل\n`;
    }

      if (r.note) {
    message += `ملاحظة: ${r.note}\n`;
  }

    // Add cheque details if available
    if (r.cheque && r.payment_type === 'شيك') {
      message += `--- تفاصيل الشيك ---\n`;
      if (r.cheque.cheque_number) message += `رقم الشيك: ${r.cheque.cheque_number}\n`;
      if (r.cheque.cheque_date) message += `تاريخ الشيك: ${r.cheque.cheque_date}\n`;
      if (bankLabel(r.cheque)) message += `البنك: ${bankLabel(r.cheque)}\n`;
      if (branchLabel(r.cheque)) message += `الفرع: ${branchLabel(r.cheque)}\n`;
      if (r.cheque.account_number) message += `رقم الحساب: ${r.cheque.account_number}\n`;
      if (r.cheque.description) message += `وصف الشيك: ${r.cheque.description}\n`;
    }

    // Add visa details if available
    if (r.visa && r.payment_type === 'فيزا') {
      message += `--- تفاصيل الفيزا ---\n`;
      if (r.visa.receipt_number) message += `رقم الإيصال: ${r.visa.receipt_number}\n`;
      if (r.visa.description) message += `الوصف: ${r.visa.description}\n`;
    }

    return message;
  };

  const handleClick = () => {
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(buildMessage(recipient))}`;
    window.open(url, '_blank');
  };

  return (
    <IconButton
      onClick={handleClick}
      sx={{
        color: '#fff',
        backgroundColor: '#25D366',
        '&:hover': {
          backgroundColor: '#1ebe5d',
        },
        width: 48,
        height: 48,
      }}
    >
      <WhatsAppIcon />
    </IconButton>
  );
};

const ViewRecipientPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false);

  const { selectedRecipient, selectedRecipientStatus, selectedRecipientError } = useSelector(state => state.recipients);

  // ✅ Date formatting function for Israel time
  const formatIsraelDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // ✅ Time formatting function
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    try {
      // Handle both full datetime and time-only strings
      if (timeString.includes('T')) {
        const date = new Date(timeString);
        return date.toLocaleTimeString('he-IL', {
          timeZone: 'Asia/Jerusalem',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } else {
        // Already a time string
        return timeString.substring(0, 8); // HH:MM:SS format
      }
    } catch {
      return timeString;
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchRecipientById(id));
    }
  }, [id, dispatch]);

  if (selectedRecipientStatus === 'loading') {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  if (selectedRecipientStatus === 'failed') {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 3 },
        }}
      >
        <Typography sx={{ color: '#DC2626', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          فشل في تحميل بيانات الدفعة: {selectedRecipientError}
        </Typography>
      </Box>
    );
  }

  if (!selectedRecipient) {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 3 },
        }}
      >
        <Typography sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          لم يتم العثور على الدفعة
        </Typography>
      </Box>
    );
  }

  const hasCheque = selectedRecipient.cheque && Object.keys(selectedRecipient.cheque).length > 0;
  const hasVisa = selectedRecipient.visa && Object.keys(selectedRecipient.visa).length > 0;
  const isChequePayment = selectedRecipient.payment_type === 'شيك';
  const isVisaPayment = selectedRecipient.payment_type === 'فيزا';
  const hasParentPhone = selectedRecipient.parent_phone && selectedRecipient.parent_phone.trim() !== '';

  const handlePrint = () => {
    exportRecipientToPrint(selectedRecipient);
  };

  const handleImageView = () => {
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                تفاصيل سند القبض
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {selectedRecipient.number ? `سند رقم ${selectedRecipient.number}` : 'عرض تفاصيل سند القبض'}
              </Typography>
              <Stack useFlexGap direction="row" spacing={1} sx={{ mt: 1.25 }}>
                {isChequePayment && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ color: '#fff !important' }} />}
                    label="دفع بالشيك"
                    sx={{
                      fontWeight: 700,
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  />
                )}
                {isVisaPayment && (
                  <Chip
                    icon={<CreditCardIcon sx={{ color: '#fff !important' }} />}
                    label="دفع بالفيزا"
                    sx={{
                      fontWeight: 700,
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack useFlexGap direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                px: 2.5,
                py: 1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              طباعة
            </Button>
            {hasParentPhone && <WhatsAppButton recipient={selectedRecipient} />}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.6)',
                px: 2.5,
                py: 1,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              رجوع
            </Button>
          </Stack>
        </MotionPaper>

        {/* Payment Type Alerts */}
        {selectedRecipient.payment_type === 'شيك' && !hasCheque && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: '#FEF3C7',
              color: '#D97706',
              border: '1px solid #FDE68A',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '& .MuiAlert-icon': { color: '#D97706' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              هذا السند مسجل كشيك ولكن لا توجد تفاصيل الشيك مرفقة
            </Typography>
          </Alert>
        )}

        {selectedRecipient.payment_type === 'فيزا' && !hasVisa && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: '#FEF3C7',
              color: '#D97706',
              border: '1px solid #FDE68A',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '& .MuiAlert-icon': { color: '#D97706' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              هذا السند مسجل كفيزا ولكن لا توجد تفاصيل الفيزا مرفقة
            </Typography>
          </Alert>
        )}

        {/* Receipt Status Alert */}
        {selectedRecipient.received && (
          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: '#DCFCE7',
              color: '#16A34A',
              border: '1px solid #BBF7D0',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '& .MuiAlert-icon': { color: '#16A34A' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              تم استلام هذا السند بنجاح
            </Typography>
          </Alert>
        )}

        {/* Main Details */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            mb: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box
              sx={{
                bgcolor: '#EEF2FF',
                borderRadius: 2,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InfoIcon sx={{ color: '#4338CA', fontSize: 22 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              معلومات السند
            </Typography>
          </Stack>

          <Grid container spacing={2} dir="rtl">
            {selectedRecipient.number && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم السند:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.number}</Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.student_name && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>اسم الطالب:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.student_name}</Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.parent_name && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>اسم ولي الأمر:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.parent_name}</Typography>
                </Stack>
              </Grid>
            )}
            {hasParentPhone && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>هاتف ولي الأمر:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.parent_phone}</Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.class_name && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الصف:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.class_name}</Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.amount && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ bgcolor: '#EEF2FF', borderRadius: 2, px: 1.5, py: 1 }}
                >
                  <Typography variant="body2" sx={{ minWidth: 108, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>المبلغ:</Typography>
                  <Typography sx={{ fontWeight: 800, color: '#4338CA', fontSize: '1.05rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {Number(selectedRecipient.amount).toLocaleString('en-US')} شيكل
                  </Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.total_school_fee !== undefined && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>إجمالي الرسوم:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {Number(selectedRecipient.total_school_fee).toLocaleString('en-US')} شيكل
                  </Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.remaining_amount !== undefined && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ bgcolor: '#FEF2F2', borderRadius: 2, px: 1.5, py: 1 }}
                >
                  <Typography variant="body2" sx={{ minWidth: 108, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الباقي على الطالب:</Typography>
                  <Typography sx={{ fontWeight: 800, color: '#DC2626', fontSize: '1.05rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {Number(selectedRecipient.remaining_amount).toLocaleString('en-US')} شيكل
                  </Typography>
                </Stack>
              </Grid>
            )}
            {selectedRecipient.payment_type && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>طريقة الدفع:</Typography>
                  <Chip
                    label={selectedRecipient.payment_type}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      bgcolor: isChequePayment ? '#E0F2FE' : isVisaPayment ? '#F3E8FF' : '#EEF2FF',
                      color: isChequePayment ? '#0284C7' : isVisaPayment ? '#7C3AED' : '#4338CA',
                    }}
                  />
                </Stack>
              </Grid>
            )}

            {selectedRecipient.note && (
              <Grid size={{ xs: 12 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="flex-start">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>ملاحظة:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.note}</Typography>
                </Stack>
              </Grid>
            )}

            {selectedRecipient.date && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>التاريخ:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{formatIsraelDate(selectedRecipient.date)}</Typography>
                </Stack>
              </Grid>
            )}
            {(selectedRecipient.time_display || selectedRecipient.time) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الوقت:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.time_display || formatTime(selectedRecipient.time)}</Typography>
                </Stack>
              </Grid>
            )}

            {selectedRecipient.created_by_name && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>منشئ السند:</Typography>
                  <Chip
                    icon={<AccountCircleIcon sx={{ color: '#0284C7 !important' }} />}
                    label={selectedRecipient.created_by_name}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      bgcolor: '#E0F2FE',
                      color: '#0284C7',
                    }}
                  />
                </Stack>
              </Grid>
            )}

            {/* Receipt Status */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 120, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>حالة الاستلام:</Typography>
                <Chip
                  label={selectedRecipient.received ? 'تم الاستلام' : 'لم يتم الاستلام'}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    bgcolor: selectedRecipient.received ? '#DCFCE7' : '#F3F4F6',
                    color: selectedRecipient.received ? '#16A34A' : '#6B7280',
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </MotionPaper>

        {/* ✅ Enhanced Cheque Details - Only show if cheque payment and has cheque data */}
        {isChequePayment && hasCheque && (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              mb: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                تفاصيل الشيك
              </Typography>
            </Stack>
            <Grid container spacing={2.5}>
              {/* Bank Details - Only show if has bank info */}
              {(bankLabel(selectedRecipient.cheque) || branchLabel(selectedRecipient.cheque) || selectedRecipient.cheque.account_number) && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#0284C7', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      بيانات البنك
                    </Typography>
                    <Stack spacing={1.75} dir="rtl">
                      {bankLabel(selectedRecipient.cheque) && (
                        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 80, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>البنك:</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{bankLabel(selectedRecipient.cheque)}</Typography>
                        </Stack>
                      )}
                      {branchLabel(selectedRecipient.cheque) && (
                        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 80, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الفرع:</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{branchLabel(selectedRecipient.cheque)}</Typography>
                        </Stack>
                      )}
                      {selectedRecipient.cheque.account_number && (
                        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 80, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الحساب:</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.cheque.account_number}</Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {/* Cheque Details - Only show if has cheque info */}
              {(selectedRecipient.cheque.cheque_number || selectedRecipient.cheque.cheque_date || selectedRecipient.cheque.cheque_image) && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#0284C7', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      بيانات الشيك
                    </Typography>
                    <Stack spacing={1.75} dir="rtl">
                      {selectedRecipient.cheque.cheque_number && (
                        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 80, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الشيك:</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.cheque.cheque_number}</Typography>
                        </Stack>
                      )}
                      {selectedRecipient.cheque.cheque_date && (
                        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 80, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>تاريخ الشيك:</Typography>
                          <Typography sx={{ fontWeight: 700, color: '#0284C7', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                            {formatIsraelDate(selectedRecipient.cheque.cheque_date)}
                          </Typography>
                        </Stack>
                      )}
                      {selectedRecipient.cheque.cheque_image && (
                        <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 80, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>صورة الشيك:</Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={handleImageView}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontFamily: 'Cairo, Tajawal, sans-serif',
                              borderColor: '#0284C7',
                              color: '#0284C7',
                              '&:hover': { borderColor: '#0284C7', bgcolor: '#E0F2FE' },
                            }}
                          >
                            عرض
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {/* Description - Only show if has description */}
              {selectedRecipient.cheque.description && (
                <Grid item xs={12} sm={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%' }}>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0284C7', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        وصف الشيك
                      </Typography>
                    </Stack>
                    <Box sx={{
                      minHeight: 80,
                      p: 1.5,
                      bgcolor: '#fff',
                      borderRadius: 2,
                      border: '1px solid #E5E7EB'
                    }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        {selectedRecipient.cheque.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </MotionPaper>
        )}

        {/* ✅ NEW: Visa Details Section - Only show if visa payment and has visa data */}
        {isVisaPayment && hasVisa && (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              mb: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  bgcolor: '#F3E8FF',
                  borderRadius: 2,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CreditCardIcon sx={{ color: '#7C3AED', fontSize: 22 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                تفاصيل الفيزا
              </Typography>
            </Stack>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#7C3AED', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    بيانات الفيزا
                  </Typography>
                  <Stack spacing={1.75} dir="rtl">
                    {selectedRecipient.visa.receipt_number && (
                      <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                        <NumbersIcon sx={{ color: '#7C3AED' }} />
                        <Typography variant="body2" sx={{ minWidth: 100, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>رقم الإيصال:</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.visa.receipt_number}</Typography>
                      </Stack>
                    )}
                    {selectedRecipient.visa.description && (
                      <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                        <DescriptionIcon sx={{ color: '#7C3AED' }} />
                        <Typography variant="body2" sx={{ minWidth: 100, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>الوصف:</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{selectedRecipient.visa.description}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </MotionPaper>
        )}

        {/* ✅ Enhanced Cheque Image Dialog */}
        <Dialog
          open={imageDialogOpen}
          onClose={handleImageDialogClose}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, fontFamily: 'Cairo, Tajawal, sans-serif' }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 800,
            fontFamily: 'Cairo, Tajawal, sans-serif',
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff'
          }}>
            صورة الشيك رقم {selectedRecipient.cheque?.cheque_number || 'غير محدد'}
            <IconButton onClick={handleImageDialogClose} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', p: 3 }}>
            {selectedRecipient.cheque?.cheque_image ? (
              <Box>
                <img
                  src={selectedRecipient.cheque.cheque_image}
                  alt="صورة الشيك"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(selectedRecipient.cheque.cheque_image, '_blank')}
                />
                <Typography variant="body2" sx={{ mt: 2, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  انقر على الصورة لعرضها في نافذة جديدة
                </Typography>
                <Button
                  variant="contained"
                  href={selectedRecipient.cheque.cheque_image}
                  target="_blank"
                  sx={{
                    mt: 2,
                    borderRadius: 2.5,
                    px: 3,
                    py: 1,
                    bgcolor: '#4338CA',
                    boxShadow: 'none',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  }}
                  startIcon={<VisibilityIcon />}
                >
                  فتح في نافذة جديدة
                </Button>
              </Box>
            ) : (
              <Typography sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                لا توجد صورة متاحة للشيك
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ViewRecipientPage;